import { KeyboardEvent, ReactNode, useId, useMemo } from 'react';
import { Box, Skeleton, Text, Tooltip, useMantineColorScheme } from '@mantine/core';
import {
    IconAdjustmentsHorizontal, IconAlertCircle, IconCalendarEvent, IconChartPie, IconCoins, IconTrendingUp,
} from '@tabler/icons-react';
import { FundDistribution } from '../../types';
import { CATALOGUE_COPY, CatalogueLanguage } from './catalogue_copy';
import {
    CATALOGUE_STATE, CatalogueFund, FundPerformance, distributionCount, distributionNotes, PortfolioProduct, activeManagementOptions,
    formatAmount, formatDate, formatNav, formatNumber, formatPercent, fundDisplayName, fundSecondaryName,
    isClosedEnded, latestDistribution, portfolioDescription, portfolioDisplayName, portfolioMinimumAmount,
    totalReturnOfCapitalPercent,
} from './catalogue_helpers';
import classes from './catalogue_cards.module.css';

const cx = (...names: (string | false | null | undefined)[]) => names.filter(Boolean).join(' ');

const SPARKLINE_WIDTH = 240;
const SPARKLINE_HEIGHT = 56;
const SPARKLINE_PADDING = 6;
const STAT_ICON_SIZE = 28;

interface SparklineProps {
    performance?: FundPerformance;
    language: CatalogueLanguage;
}

/**
 * Compact NAV trend.
 *
 * Rendered as a labelled `img` with a text summary rather than `aria-hidden`: the trend
 * is the only place a card states direction of travel, so hiding it from assistive tech
 * removes information instead of removing noise.
 */
export const FundSparkline = ({ performance, language }: SparklineProps) => {
    const copy = CATALOGUE_COPY[language];
    const gradientId = useId();
    const { colorScheme } = useMantineColorScheme();

    const geometry = useMemo(() => {
        const values = performance?.values || [];
        if (values.length < 2) return null;
        const min = Math.min(...values);
        const max = Math.max(...values);
        const range = max - min || 1;
        const usableHeight = SPARKLINE_HEIGHT - SPARKLINE_PADDING * 2;
        const points = values.map((value, index) => {
            const x = (index / (values.length - 1)) * SPARKLINE_WIDTH;
            const y = SPARKLINE_HEIGHT - SPARKLINE_PADDING - ((value - min) / range) * usableHeight;
            return { x, y };
        });
        return {
            values,
            line: points.map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(' '),
            area: `0,${SPARKLINE_HEIGHT} ${points.map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(' ')} ${SPARKLINE_WIDTH},${SPARKLINE_HEIGHT}`,
            last: points[points.length - 1],
            rising: values[values.length - 1] >= values[0],
        };
    }, [performance?.values]);

    if (performance?.loading) return <Skeleton height={SPARKLINE_HEIGHT} mt="sm" radius="sm" />;

    if (!geometry) {
        return (
            <Box h={SPARKLINE_HEIGHT} mt="sm" style={{ display: 'flex', alignItems: 'center' }}>
                <Text size="xs" c="dimmed">{copy.noPerformance}</Text>
            </Box>
        );
    }

    // Brand palette: the line is drawn in Hawk Gray on both schemes; direction of travel
    // is carried by the YTD figure beside it and by the text summary below.
    const stroke = '#aa9f8e';
    const summary = copy.performanceSummary
        .replace('{from}', formatNav(geometry.values[0], language))
        .replace('{to}', formatNav(geometry.values[geometry.values.length - 1], language))
        .replace('{count}', formatNumber(geometry.values.length, language));

    return (
        <Tooltip label={summary} multiline w={260} withArrow position="top">
            <Box mt="sm" h={SPARKLINE_HEIGHT} role="img" aria-label={`${copy.performance}. ${summary}`}>
                <svg
                    width="100%"
                    height={SPARKLINE_HEIGHT}
                    viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
                    preserveAspectRatio="none"
                    focusable="false"
                >
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={stroke} stopOpacity={colorScheme === 'dark' ? 0.35 : 0.22} />
                            <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <polygon points={geometry.area} fill={`url(#${gradientId})`} />
                    <polyline
                        points={geometry.line}
                        fill="none"
                        stroke={stroke}
                        strokeWidth={2.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                    />
                    {/* Non-scaling radius keeps the marker round despite the stretched viewBox. */}
                    <circle cx={geometry.last.x} cy={geometry.last.y} r={3} fill={stroke} vectorEffect="non-scaling-stroke" />
                </svg>
            </Box>
        </Tooltip>
    );
};

// ---- Shared building blocks -------------------------------------------------------------

/** Path of the KFH Capital pinwheel mark, lifted from `public/logo.svg` (mark only, no wordmark). */
const KFH_MARK_PATH = 'M 40.1 193.6 L 82.9 150.8 L 82.9 253.6 L 57.2 253.6 C 47.6 253.6 40.1 246.1 40.1 236.4 Z M 185.6 253.5 L 228.4 253.5 C 238.0 253.5 245.5 246.0 245.5 236.3 L 245.5 210.7 L 142.8 210.7 Z M 257.7 120.5 L 185.1 193.2 L 245.6 193.2 L 275.9 162.9 C 282.5 156.2 282.5 145.3 275.9 138.7 Z M 100.4 253.7 L 130.7 283.9 C 137.4 290.6 148.3 290.6 154.9 283.9 L 173.1 265.8 L 100.4 193.2 Z M 245.4 65.2 C 245.4 55.6 237.9 48.1 228.2 48.1 L 202.6 48.1 L 202.6 150.8 L 245.4 108.1 Z M 185.1 47.9 L 154.8 17.7 C 148.1 11.0 137.3 11.0 130.6 17.7 L 112.4 35.9 L 185.1 108.5 Z M 100.0 48.1 L 57.2 48.1 C 47.5 48.1 40.0 55.6 40.0 65.3 L 40.0 90.9 L 142.8 90.9 Z M 39.9 108.5 L 9.6 138.8 C 2.9 145.5 2.9 156.3 9.6 163.0 L 27.8 181.2 L 100.4 108.5 Z';

/**
 * Brand flourish in the card's top end corner: the logo mark, cropped by the card and
 * tinted Hawk Gray. Purely decorative, so hidden from assistive tech.
 */
const KfhMark = () => (
    <svg className={classes.mark} viewBox="2 10 281 281" aria-hidden="true" focusable="false">
        <path d={KFH_MARK_PATH} fill="currentColor" />
    </svg>
);

interface CatalogueCardProps {
    label: string;
    onOpen: () => void;
    children: ReactNode;
}

/**
 * Card shell. Cards open a detail view, so they behave like buttons for keyboard and
 * screen reader users rather than being a `div` with `cursor: pointer`.
 */
const CatalogueCard = ({ label, onOpen, children }: CatalogueCardProps) => (
    <div
        className={classes.card}
        role="button"
        tabIndex={0}
        aria-label={label}
        onClick={onOpen}
        onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen();
            }
        }}
    >
        <KfhMark />
        {children}
    </div>
);

const Chip = ({ children, tone }: { children: ReactNode; tone?: 'accent' | 'warn' }) => (
    <span className={cx(classes.chip, tone === 'accent' && classes.chipAccent, tone === 'warn' && classes.chipWarn)}>
        {children}
    </span>
);

interface StatProps {
    icon: ReactNode;
    label: string;
    value: string;
    /** Small line under the value, e.g. the date a figure was recorded. */
    sub?: string;
    tone?: 'positive' | 'negative';
}

const Stat = ({ icon, label, value, sub, tone }: StatProps) => (
    <div className={classes.stat}>
        <span className={classes.statIcon}>{icon}</span>
        <div className={classes.statText}>
            <div className={classes.statLabel}>{label}</div>
            <div className={cx(classes.statValue, tone === 'positive' && classes.statPositive, tone === 'negative' && classes.statNegative)}>
                {value}
            </div>
            {sub && <div className={classes.statSub}>{sub}</div>}
        </div>
    </div>
);

// ---- Fund card ----------------------------------------------------------------------------

interface FundCardProps {
    fund: CatalogueFund;
    language: CatalogueLanguage;
    performance?: FundPerformance;
    /** From the fund's `dividends` subcollection, not the legacy array on the document. */
    dividends?: FundDistribution[];
    onOpen: () => void;
}

export const FundCard = ({ fund, language, performance, dividends, onOpen }: FundCardProps) => {
    const copy = CATALOGUE_COPY[language];
    const secondaryName = fundSecondaryName(fund, language);
    const ytd = Number.isFinite(fund.ytd) ? Number(fund.ytd) : null;
    const closedEnded = isClosedEnded(fund);
    const latest = latestDistribution(dividends);
    const dividendCount = distributionCount(dividends);
    // The banner carries the notes recorded against the latest distribution — the same
    // text the drawer shows under "Notes" — e.g. an expected exit date.
    const update = distributionNotes(latest, language);

    // selectCatalogueFunds guarantees a real type; guarded anyway so a bad document
    // degrades to a missing badge rather than a crash.
    const typeLabel = fund.type === 'open_ended' ? copy.openEnded : fund.type === 'closed_ended' ? copy.closedEnded : null;

    /* Open-ended funds without a declared frequency still have a useful date: the NAV date. */
    const metaLine = fund.distribution_frequency
        ? `${copy.distributionFrequency}: ${copy[fund.distribution_frequency]}`
        : !closedEnded && fund.nav_date
            ? `${copy.navDate}: ${formatDate(fund.nav_date, language)}`
            : null;

    return (
        <CatalogueCard label={`${fundDisplayName(fund, language)} — ${copy.viewFund}`} onOpen={onOpen}>
            <div className={classes.header}>
                <h3 className={cx(classes.title, classes.clamp2)}>{fundDisplayName(fund, language)}</h3>
                {typeLabel && <span className={classes.typeBadge}>{typeLabel}</span>}
            </div>
            {secondaryName && (
                <div className={cx(classes.subtitle, classes.clamp1)} dir="auto">{secondaryName}</div>
            )}

            {/* States are shown, never hidden — an RM needs to see why a product can or
                cannot be sold. The sellable state gets the accent chip, others a warning. */}
            <div className={classes.chips}>
                {fund.currency && <Chip>{fund.currency}</Chip>}
                {fund.asset_class?.map((item) => <Chip key={String(item)}>{String(item)}</Chip>)}
                {fund.private && <Chip>{copy.privateFund}</Chip>}
                {fund.professionalOnly && <Chip tone="warn">{copy.professionalOnly}</Chip>}
                {fund.fund_state === CATALOGUE_STATE && <Chip tone="accent">{copy.active}</Chip>}
                {fund.fund_state && fund.fund_state !== CATALOGUE_STATE && (
                    <Chip tone="warn">{fund.fund_state.replaceAll('_', ' ')}</Chip>
                )}
            </div>

            {metaLine && (
                <div className={classes.meta}>
                    <IconCalendarEvent size={22} stroke={1.75} className={classes.metaIcon} />
                    <span>{metaLine}</span>
                </div>
            )}

            <div className={classes.body}>
                {update && (
                    <div className={classes.banner} role="note">
                        <span className={classes.bannerIcon}><IconAlertCircle size={24} stroke={2.25} /></span>
                        <div>
                            <div className={classes.bannerKicker}>{copy.importantUpdate}</div>
                            <div className={classes.bannerTitle}>{update}</div>
                        </div>
                    </div>
                )}

                {/* Closed-ended funds have no NAV series to draw; their distributions are
                    the performance signal and live in the stat tiles below. */}
                {!closedEnded && (
                    <div className={classes.trend}>
                        <FundSparkline performance={performance} language={language} />
                    </div>
                )}
            </div>

            {closedEnded ? (
                <div className={classes.stats}>
                    <Stat
                        icon={<IconCoins size={STAT_ICON_SIZE} stroke={1.5} />}
                        label={copy.latestDistribution}
                        value={latest ? formatPercent(latest.percentage, language) : '—'}
                    />
                    <Stat
                        icon={<IconCalendarEvent size={STAT_ICON_SIZE} stroke={1.5} />}
                        label={copy.latestDistributionDate}
                        value={latest ? formatDate(latest.date, language) : '—'}
                    />
                    <Stat
                        icon={<IconChartPie size={STAT_ICON_SIZE} stroke={1.5} />}
                        label={copy.totalReturnOfCapital}
                        value={formatPercent(totalReturnOfCapitalPercent(dividends), language)}
                    />
                </div>
            ) : (
                <div className={classes.stats}>
                    <Stat
                        icon={<IconCoins size={STAT_ICON_SIZE} stroke={1.5} />}
                        label={copy.nav}
                        value={formatNav(fund.nav_per_unit, language)}
                    />
                    <Stat
                        icon={<IconTrendingUp size={STAT_ICON_SIZE} stroke={1.5} />}
                        label={copy.ytd}
                        value={formatPercent(fund.ytd, language)}
                        tone={ytd == null ? undefined : ytd >= 0 ? 'positive' : 'negative'}
                    />
                    {/* A bare count told an RM a fund had dividends without showing any of
                        them, so the latest one takes the tile whenever there is one. */}
                    {latest ? (
                        <Stat
                            icon={<IconChartPie size={STAT_ICON_SIZE} stroke={1.5} />}
                            label={copy.latestDistribution}
                            value={formatPercent(latest.percentage, language)}
                            sub={formatDate(latest.date, language)}
                        />
                    ) : (
                        <Stat
                            icon={<IconChartPie size={STAT_ICON_SIZE} stroke={1.5} />}
                            label={copy.dividends}
                            value={formatNumber(dividendCount, language)}
                        />
                    )}
                </div>
            )}
        </CatalogueCard>
    );
};

// ---- Portfolio card -----------------------------------------------------------------------

interface PortfolioCardProps {
    portfolio: PortfolioProduct;
    language: CatalogueLanguage;
    onOpen: () => void;
}

export const PortfolioCard = ({ portfolio, language, onOpen }: PortfolioCardProps) => {
    const copy = CATALOGUE_COPY[language];
    const description = portfolioDescription(portfolio, language);
    const options = activeManagementOptions(portfolio);
    const minimum = portfolioMinimumAmount(portfolio);
    const secondaryName = (language === 'ar' ? portfolio.name_en : portfolio.name_ar) || '';

    return (
        <CatalogueCard label={portfolioDisplayName(portfolio, language)} onOpen={onOpen}>
            <div className={classes.header}>
                <h3 className={cx(classes.title, classes.clamp2)}>{portfolioDisplayName(portfolio, language)}</h3>
                <span className={classes.typeBadge}>{copy.portfolioService}</span>
            </div>
            {secondaryName && (
                <div className={cx(classes.subtitle, classes.clamp1)} dir="auto">{secondaryName}</div>
            )}

            <div className={classes.chips}>
                {portfolio.portfolio_type && <Chip>{portfolio.portfolio_type}</Chip>}
                <Chip tone="accent">{copy.active}</Chip>
            </div>

            <div className={classes.body}>
                <p className={cx(classes.description, classes.clamp3)}>{description || copy.noDescription}</p>
            </div>

            <div className={cx(classes.stats, classes.statsTwo)}>
                <Stat
                    icon={<IconCoins size={STAT_ICON_SIZE} stroke={1.5} />}
                    label={copy.minimumAmount}
                    value={minimum ? formatAmount(minimum.amount, language, minimum.currency) : '—'}
                />
                <Stat
                    icon={<IconAdjustmentsHorizontal size={STAT_ICON_SIZE} stroke={1.5} />}
                    label={copy.managementOptions}
                    value={formatNumber(options.length, language)}
                />
            </div>
        </CatalogueCard>
    );
};
